import { useNavigate, useSearch } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';

import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { useOrganizationColumns } from '../components/organization-columns';
import { organizationFilters } from '../components/organization-filters';
import { ProvisionOrganizationDialog } from '../components/provision-organization-dialog';
import { useOrganizations } from '../hooks/use-organizations';

const ROUTE_ID = '/_authed/organizations/' as const;
const ROUTE_PATH = '/organizations/' as const;

function OrganizationsList() {
  const { t } = useTranslation(['organizations', 'common']);
  const { me } = session.useSession();
  const canManage = canManageOrganizations(me?.user.role);
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });

  const filters = useMemo(() => organizationFilters({ t }), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useOrganizations(list.params);
  const columns = useOrganizationColumns();
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'organizations',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
        actions={canManage ? <ProvisionOrganizationDialog tour="list" /> : undefined}
      />
      {isEmptyList ? (
        <EmptyState
          tour="platform.organizations.empty"
          tourLink
          icon={Building2}
          title={t('empty.title')}
          description={t('empty.description')}
          action={canManage ? <ProvisionOrganizationDialog /> : undefined}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/organizations/$organizationId',
              params: { organizationId: row.id },
            })
          }
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{
            table: 'platform.organizations.table',
            search: 'platform.organizations.search',
            filters: 'platform.organizations.filters',
          }}
        />
      )}
    </PageContent>
  );
}

export function OrganizationsListPage() {
  return (
    <FeatureErrorBoundary name="organizations.list">
      <OrganizationsList />
    </FeatureErrorBoundary>
  );
}
